import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import TrustBadge from "@/components/TrustBadge";
import { ShieldCheck, CheckCircle2, Circle, ArrowRight, Sparkles } from "lucide-react";

/**
 * "Boost your trust score" — gamified nudge card on the Dashboard.
 *
 * Data source: GET /api/me/trust-nudges
 * Renders the caller's current TrustBadge + a checklist of pending actions ranked by
 * points-per-effort. Tapping a nudge navigates to the page that lets the user complete
 * it. Completed items collapse into a subtle "Done" section for reassurance.
 */
export default function TrustNudgeCard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.get("/me/trust-nudges").then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  const pending = data.nudges.filter(n => !n.done);
  const done = data.nudges.filter(n => n.done);
  const topPending = pending.slice(0, expanded ? pending.length : 3);

  return (
    <div className="mt-8 rounded-3xl border border-[#E5D5C0] bg-gradient-to-br from-white via-[#FFF9F4] to-[#FFF3EC] p-6 md:p-7 shadow-sm" data-testid="trust-nudge-card">
      {/* Header — current score + achievable */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#8B5E34]">
            <Sparkles size={13} /> Boost your trust score
          </div>
          <h3 className="heading text-2xl md:text-3xl font-extrabold text-stone-900 mt-1">
            You could earn <span className="text-[#E05E36]">+{data.achievable_points}</span> more points
          </h3>
          <p className="text-sm text-stone-600 mt-1">
            Buyers see a colored badge on your listings. Higher trust → more bookings.
          </p>
        </div>
        <div className="shrink-0">
          <TrustBadge trust={data.current} size="md" />
        </div>
      </div>

      {/* Pending checklist */}
      {topPending.length > 0 && (
        <ul className="mt-5 space-y-2" data-testid="trust-nudge-pending-list">
          {topPending.map(n => (
            <li key={n.id}>
              <button
                onClick={() => navigate(n.url)}
                className="w-full flex items-center gap-3 text-left rounded-2xl border border-stone-200 bg-white hover:border-[#E05E36] hover:shadow-md transition-all px-4 py-3"
                data-testid={`trust-nudge-${n.id}`}
              >
                <Circle size={18} className="text-stone-300 shrink-0" strokeWidth={2.5} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-stone-900 truncate">{n.label}</div>
                </div>
                <span className="shrink-0 text-xs font-black uppercase tracking-widest text-[#E05E36] bg-orange-50 border border-orange-200 rounded-full px-2 py-0.5">
                  +{n.points}
                </span>
                <ArrowRight size={16} className="text-stone-400 shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Expand / collapse pending */}
      {pending.length > 3 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 text-xs font-bold text-[#2D5A27] hover:underline"
          data-testid="trust-nudge-expand"
        >
          {expanded ? "Show fewer" : `Show ${pending.length - 3} more`}
        </button>
      )}

      {/* Done section — collapsed by default, small + reassuring */}
      {done.length > 0 && (
        <div className="mt-5 pt-4 border-t border-stone-200/70">
          <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-2">
            <CheckCircle2 size={11} className="inline mr-1 text-emerald-500" strokeWidth={3} />
            {done.length} completed
          </div>
          <div className="flex flex-wrap gap-1.5">
            {done.map(n => (
              <span key={n.id}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                    title={n.label}>
                <CheckCircle2 size={10} strokeWidth={3} /> {n.label.replace(/\s*\(.+?\)$/, "")}
              </span>
            ))}
          </div>
        </div>
      )}

      {pending.length === 0 && (
        <div className="mt-5 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm font-semibold px-4 py-3 flex items-center gap-2" data-testid="trust-nudge-all-done">
          <ShieldCheck size={18} className="text-emerald-600" /> All trust nudges complete — nice work!
        </div>
      )}
    </div>
  );
}
